import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Always start in light mode by default; saved preference is restored after mount
  const [darkMode, setDarkMode] = useState(false);

  // Restore saved theme from localStorage (so returning users keep their choice)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('darkMode');
      if (saved === 'true') setDarkMode(true);
    } catch (error) {
      console.error('Error reading theme preference from localStorage:', error);
    }
  }, []);

  // Apply theme to document immediately on mount and when darkMode changes
  useEffect(() => {
    // Apply theme classes to document
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [darkMode]);

  // Save preference to localStorage whenever user explicitly changes it
  const setDarkModeWithPersistence = (value) => {
    setDarkMode(value);
    // Always save user's explicit choice to localStorage
    // This ensures preference persists across logout/login
    try {
      localStorage.setItem('darkMode', JSON.stringify(value));
    } catch (error) {
      console.error('Error saving theme preference to localStorage:', error);
    }
  };

  const toggleDarkMode = () => {
    setDarkModeWithPersistence(!darkMode);
  };

  const value = {
    darkMode,
    setDarkMode: setDarkModeWithPersistence,
    toggleDarkMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

